/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {vec3} from "gl-matrix";
import MaterialLibrary from "App/geometry/material/material_library";
import IRenderDocument from "App/render_document_interface";
import Model from "App/model";
import Factory from "App/geometry/objects/factory";
import {RenderElement} from "App/model/elements/element";
import RenderObject from "App/geometry/objects/object";
import {inURLBinaryData} from "App/storage/utils";

export default
class Description {
    private _document: IRenderDocument;
    private _model: Model;
    private _geometryObjects: RenderObject[] = [];
    private _viewpoint: any;

    private _geometryObjectsMap = {}; // this is for fast finding of geometry object in the model

    constructor(aDocument: IRenderDocument, aModel: Model) {
        this._document = aDocument;
        this._model = aModel;
        this._geometryObjects = [];
        this._viewpoint = null;

        this._geometryObjectsMap = {}; // this is for fast finding of geometry object in the model
    }

    public addObject(aObjectJSON: any, aElement: RenderElement) {
        let geomObject = Factory.create(aObjectJSON.description.type);
        geomObject.setModel(this._model);
        geomObject.parse(this._model.getModelDescription(), aObjectJSON, aElement);
        this._geometryObjects.push(geomObject);

        this._geometryObjectsMap[geomObject.getUID()] = geomObject;

        return geomObject;
    }

    public removeObject(aGeometryObject: RenderObject) {
        let searchedUID = aGeometryObject.getUID();
        if (this._geometryObjectsMap[searchedUID] !== undefined) {
            delete this._geometryObjectsMap[searchedUID];
        }

        for (let i = 0; i < this._geometryObjects.length; i++) {
            if (this._geometryObjects[i].getUID() === searchedUID) {
                this._geometryObjects.splice(i, 1);
                return true;
            }
        }

        return false;
    }

    public getViewpoint(): any {
        return this._viewpoint;
    }

    public  getGeometryObjects(): RenderObject[] {
        return this._geometryObjects;
    }

    public getGeometryObjectByUID(aGeometryObjectUID: number): RenderObject {
        let geometryObject = this._geometryObjectsMap[aGeometryObjectUID];

        if (geometryObject !== undefined) {
            return geometryObject;
        }

        return null;
    }

    public parse(aModelGeometryDescriptionJSON: any) {
        if (aModelGeometryDescriptionJSON.exportInfo !== undefined) {
            this._model.getModelDescription().projectInfo.setFromExportInfoCompatibility(aModelGeometryDescriptionJSON.exportInfo);
        }

        if (aModelGeometryDescriptionJSON.viewpoint !== undefined) {
            this._viewpoint = aModelGeometryDescriptionJSON.viewpoint;
        }

        if (aModelGeometryDescriptionJSON.hasOwnProperty('materialLibrary')) {
            this._model.getMaterialLibrary().parse(this._model.getModelDescription(), aModelGeometryDescriptionJSON.materialLibrary);
        }

        let objects = aModelGeometryDescriptionJSON.shapes;
        for (let objectJSON of objects) {
            // check if the object is repeated in model description (repetition transformation is used later)
            if (objectJSON.description && objectJSON.description.id !== undefined) {
                let elements = this._model.getElementsByShapeID(objectJSON.description.id);
                for (let element of elements) {
                    // this is applied for geometry parsed from added model to scene as we match the geometry object and model element by properties.id
                    this.addObject(objectJSON, element);
                }
            }
        }
    }

    public getJSONRepresentation() {
        let result: any = {};
        if (this._viewpoint) {
            result.viewpoint = {
                position: vec3.clone(this._viewpoint.position),
                centerOfRotation: vec3.clone(this._viewpoint.centerOfRotation),
                fieldOfView: this._viewpoint.fieldOfView
            };
        }

        // save material library
        // collect id of all materials used in this model
        let usedMaterialIDsInModelMap = {};
        let members = MaterialLibrary.getMaterialMembers();
        for (let geometryObject of this._geometryObjects) {
            if (geometryObject.appearance) {
                for (let member of  members) {
                    let material = geometryObject.appearance[member];
                    if (material) {
                        usedMaterialIDsInModelMap[material.getUID()] = true;
                    }
                }
            }
        }

        // save material library with materials used in this model only
        result.materialLibrary = {};
        let categories = MaterialLibrary.getCategories();
        for (let category of categories) {
            if (this._model.getMaterialLibrary().hasOwnProperty(category)) {
                result.materialLibrary[category] = [];
                for (let matid in this._model.getMaterialLibrary()[category]) {
                    if (usedMaterialIDsInModelMap[matid] !== undefined) {
                        result.materialLibrary[category].push(this._model.getMaterialLibrary()[category][matid].getJSONRepresentation());
                    }
                }
            }
        }

        // save shapes
        result.shapes = [];
        for (let geometryObject of  this._geometryObjects) {
            // at this moment only shapes are implemented
            result.shapes.push(geometryObject.getJSONRepresentation());
        }

        let resultStringified = JSON.stringify(result);
        let base64Result = inURLBinaryData() + window.btoa(resultStringified);

        return base64Result;
    }
}
